import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  ClipboardList, LogOut, CheckCircle, Clock, AlertCircle, Loader2,
  FileText, Phone, MapPin, User, ChevronRight, ArrowLeft, Menu, X,
  Send, CheckSquare, Play, RotateCcw, Globe
} from 'lucide-react';

function renderPayloadFields(obj, depth = 0) {
  if (obj === null || obj === undefined) return <span className="text-[#8696a0]">—</span>;
  if (typeof obj === 'string') return <span className="text-white">{obj}</span>;
  if (typeof obj === 'number' || typeof obj === 'boolean') return <span className="text-white">{String(obj)}</span>;
  if (Array.isArray(obj)) {
    if (obj.length === 0) return <span className="text-[#8696a0]">[]</span>;
    return (
      <div className="space-y-1">
        {obj.map((item, i) => (
          <div key={i} className="border-l border-[#374045] pl-2 ml-1">
            <span className="text-[10px] text-[#8696a0]">[{i}]</span>
            {renderPayloadFields(item, depth + 1)}
          </div>
        ))}
      </div>
    );
  }
  if (typeof obj === 'object') {
    const entries = Object.entries(obj);
    if (entries.length === 0) return <span className="text-[#8696a0]">{'{}'}</span>;
    return (
      <div className="space-y-1">
        {entries.map(([key, val]) => (
          <div key={key} className="grid grid-cols-[160px_1fr] gap-2">
            <span className="text-[#8696a0] truncate">{key}</span>
            <span>{renderPayloadFields(val, depth + 1)}</span>
          </div>
        ))}
      </div>
    );
  }
  return <span className="text-white">{String(obj)}</span>;
}

function TaskCard({ task, onSelect, onComplete }) {
  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    in_progress: 'bg-blue-500/20 text-blue-400',
    completed: 'bg-green-500/20 text-green-400',
    urgent: 'bg-red-500/20 text-red-400',
  };

  return (
    <div 
      onClick={() => onSelect(task)}
      className="bg-[#111b21] rounded-xl border border-[#2a3942] p-4 cursor-pointer hover:border-[#00a884] transition"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <p className="text-white font-medium text-sm">{task.title}</p>
          <p className="text-[#8696a0] text-xs mt-1">{task.case_number || task.case_id?.slice(0, 8)} · {task.service_type || ''}</p>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs ${statusColors[task.status] || 'bg-gray-500/20 text-gray-400'}`}>
          {task.status}
        </span>
      </div>
      <div className="flex items-center gap-3 text-xs text-[#8696a0]">
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {task.due_date || 'No due date'}</span>
        <span className="flex items-center gap-1"><User className="w-3 h-3" /> {task.client_name || 'Anonymous'}</span>
      </div>
      {task.status !== 'completed' && (
        <button
          onClick={(e) => { e.stopPropagation(); onComplete(task.id); }}
          className="mt-3 w-full py-2 bg-[#00a884]/20 text-[#00a884] rounded-lg text-xs font-medium hover:bg-[#00a884]/30 transition flex items-center justify-center gap-1"
        >
          <CheckSquare className="w-3 h-3" /> Mark Complete
        </button>
      )}
    </div>
  );
}

export default function AgentPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [taskNotes, setTaskNotes] = useState('');
  const [caseDetails, setCaseDetails] = useState(null);
  const [autoProfile, setAutoProfile] = useState(null);
  const [autoResult, setAutoResult] = useState(null);
  const [autoRunning, setAutoRunning] = useState(false);
  const [artifacts, setArtifacts] = useState([]);
  const [secureDocs, setSecureDocs] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [payloadData, setPayloadData] = useState(null);
  const [editedPayload, setEditedPayload] = useState('');
  const [editingPayload, setEditingPayload] = useState(false);

  useEffect(() => {
    if (!user || !['agent', 'admin'].includes(user.role?.toLowerCase())) {
      navigate('/login/staff');
      return;
    }
    fetchTasks();
  }, [user, navigate, activeTab]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/agent/tasks?status=${activeTab !== 'all' ? activeTab : ''}`);
      const rawTasks = data.tasks || [];
      if (rawTasks.length > 0) {
        try {
          const casesRes = await api.get('/agent/cases');
          const caseMap = {};
          (casesRes.data || []).forEach(c => { caseMap[c.id] = c; });
          const enriched = rawTasks.map(t => {
            const c = caseMap[t.case_id] || {};
            return { ...t, client_name: c.client_name || t.client_name || '—', case_number: c.case_number || t.case_id?.slice(0, 8), service_type: c.service_type || '—' };
          });
          setTasks(enriched);
        } catch (e) {
          setTasks(rawTasks);
        }
        return;
      }
      setTasks(rawTasks);
    } catch (err) {
      toast.error('Failed to fetch tasks');
      try {
        const { data } = await api.get('/cases');
        const convertedTasks = (data.cases || []).map(c => ({
          id: c.id,
          title: `${c.service_type || 'General'} - ${c.client_name || 'Anonymous'}`,
          case_id: c.id,
          status: c.status === 'completed' ? 'completed' : c.status === 'assigned' ? 'in_progress' : 'pending',
          client_name: c.client_name,
          due_date: new Date(c.created_at).toLocaleDateString(),
          description: `Case for ${c.service_type || 'general'} service`,
        }));
        setTasks(convertedTasks);
      } catch (e) {
        toast.error('Fallback case fetch also failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const selectTask = async (task) => {
    setSelectedTask(task);
    setAutoResult(null);
    setAutoProfile(null);
    setSidebarOpen(false);
    try {
      const { data } = await api.get(`/cases/${task.case_id}`);
      setCaseDetails(data);
      // Load automation profile if linked
      if (data.template_pack_id) {
        try {
          const p = await api.get(`/admin/templates/${data.template_pack_id}/automation-profile`);
          setAutoProfile(p.data.profile || null);
        } catch (e) {}
      }
    } catch (err) {
      setCaseDetails(null);
    }
    fetchArtifacts(task.case_id);
    fetchSecureDocs(task.case_id);
    fetchChatHistory(task.case_id);
    fetchPayload(task.case_id);
  };

  const fetchArtifacts = async (caseId) => {
    try {
      const { data } = await api.get(`/admin/cases/${caseId}/artifacts`);
      setArtifacts(data.artifacts || []);
    } catch (err) {
      setArtifacts([]);
    }
  };

  const fetchSecureDocs = async (caseId) => {
    try {
      const { data } = await api.get(`/documents/${caseId}/list`);
      setSecureDocs(data.documents || []);
    } catch (err) {
      setSecureDocs([]);
    }
  };

  const fetchChatHistory = async (caseId) => {
    try {
      const { data } = await api.get(`/chat/history/${caseId}`);
      setChatHistory(data.messages || []);
    } catch (err) {
      setChatHistory([]);
    }
  };

  const fetchPayload = async (caseId) => {
    try {
      const { data } = await api.get(`/admin/cases/${caseId}/automation-payload`);
      if (data && data.payload_json) {
        setPayloadData(data);
        setEditedPayload(JSON.stringify(data.payload_json, null, 2));
        return;
      }
    } catch (err) {}
    if (caseDetails?.efiling_context?.pdf_available_payload) {
      const fallback = { payload_json: caseDetails.efiling_context.pdf_available_payload, payload_status: caseDetails.efiling_context.status || 'extracted' };
      setPayloadData(fallback);
      setEditedPayload(JSON.stringify(fallback.payload_json, null, 2));
    }
  };

  const savePayload = async () => {
    try {
      const parsed = JSON.parse(editedPayload);
      await api.patch(`/admin/cases/${selectedTask.case_id}/automation-payload`, { payload_json: parsed });
      setPayloadData({ ...payloadData, payload_json: parsed });
      setEditingPayload(false);
      toast.success('Payload updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save payload');
    }
  };

  const runAutomation = async () => {
    if (!selectedTask) return;
    setAutoRunning(true);
    setAutoResult(null);
    try {
      const { data } = await api.post(`/agent/cases/${selectedTask.case_id}/execute-automation`);
      setAutoResult(data);
      toast.success('Automation triggered');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Automation failed');
      setAutoResult({ error: err.response?.data?.detail || 'Execution failed' });
    } finally {
      setAutoRunning(false);
    }
  };

  const completeTask = async (taskId) => {
    try {
      await api.patch(`/agent/tasks/${taskId}`, { status: 'completed', notes: taskNotes });
      fetchTasks();
      if (selectedTask?.id === taskId) {
        setSelectedTask({ ...selectedTask, status: 'completed' });
      }
    } catch (err) {
      toast.error('Failed to complete task');
      // Fallback: update case status using case_id from the task
      const task = tasks.find(t => t.id === taskId);
      if (task?.case_id) {
        try {
          await api.patch(`/cases/${task.case_id}`, { status: 'completed' });
          toast.success('Case marked completed as fallback');
          fetchTasks();
        } catch (e) {
          toast.error('Fallback complete failed');
        }
      }
    }
  };

  const updateTaskStatus = async (status) => {
    if (!selectedTask) return;
    try {
      await api.patch(`/agent/tasks/${selectedTask.id}`, { status, notes: taskNotes });
      fetchTasks();
      setSelectedTask({ ...selectedTask, status });
    } catch (err) {
      toast.error('Failed to update task');
    }
  };

  const filteredTasks = tasks.filter(t => {
    if (activeTab === 'pending') return t.status === 'pending' || t.status === 'in_progress';
    if (activeTab === 'completed') return t.status === 'completed';
    return true;
  });

  return (
    <div className="min-h-screen bg-[#0b141a] text-white">
      {/* Mobile Header */}
      <div className="lg:hidden bg-[#111b21] border-b border-[#2a3942] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#00a884] rounded-lg flex items-center justify-center">
            <ClipboardList className="w-4 h-4 text-white" />
          </div>
          <div>
            <span className="font-bold">Agent Portal</span>
            <p className="text-xs text-[#8696a0]">{user?.email}</p>
          </div>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar - Task List */}
        <aside className={`${sidebarOpen ? 'fixed inset-0 z-50 bg-[#111b21]' : 'hidden'} lg:block lg:static lg:w-80 lg:h-screen lg:border-r lg:border-[#2a3942] flex-shrink-0 overflow-y-auto`}>
          <div className="p-4">
            <div className="hidden lg:flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#00a884] rounded-xl flex items-center justify-center">
                  <ClipboardList className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="font-bold">Agent</h1>
                  <p className="text-xs text-[#8696a0]">{user?.email}</p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-4">
              <button 
                onClick={() => setActiveTab('pending')}
                className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition ${
                  activeTab === 'pending' ? 'bg-[#00a884] text-white' : 'bg-[#202c33] text-[#8696a0]'
                }`}
              >
                Active
              </button>
              <button 
                onClick={() => setActiveTab('completed')}
                className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition ${
                  activeTab === 'completed' ? 'bg-[#00a884] text-white' : 'bg-[#202c33] text-[#8696a0]'
                }`}
              >
                Completed
              </button>
              <button 
                onClick={() => setActiveTab('all')}
                className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition ${
                  activeTab === 'all' ? 'bg-[#00a884] text-white' : 'bg-[#202c33] text-[#8696a0]'
                }`}
              >
                All
              </button>
            </div>

            {/* Task List */}
            <div className="space-y-3">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#00a884]" />
                </div>
              ) : filteredTasks.length === 0 ? (
                <div className="text-center py-8 text-[#8696a0] text-sm">
                  <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  No tasks found
                </div>
              ) : (
                filteredTasks.map(t => (
                  <TaskCard key={t.id} task={t} onSelect={selectTask} onComplete={completeTask} />
                ))
              )}
            </div>

            <div className="mt-6 pt-4 border-t border-[#2a3942]">
              <button 
                onClick={logout}
                className="flex items-center gap-3 px-4 py-3 text-[#aebac1] hover:text-red-400 transition w-full"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-sm font-medium">Logout</span>
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content - Task Detail */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          {!selectedTask ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ClipboardList className="w-16 h-16 text-[#374045] mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Select a Task</h2>
              <p className="text-[#8696a0] text-sm">Choose a task from the sidebar to view details and take action</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Task Header */}
              <div className="flex items-start justify-between">
                <div>
                  <button 
                    onClick={() => setSelectedTask(null)}
                    className="lg:hidden flex items-center gap-1 text-[#8696a0] text-sm mb-2"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <h2 className="text-2xl font-bold text-white">{selectedTask.title}</h2>
                  <p className="text-[#8696a0] text-sm mt-1">Case: {selectedTask.case_number || selectedTask.case_id?.slice(0, 8)}</p>
                </div>
                <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                  selectedTask.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                  selectedTask.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                  selectedTask.status === 'urgent' ? 'bg-red-500/20 text-red-400' :
                  'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {selectedTask.status}
                </span>
              </div>

              {/* Task Description */}
              <div className="bg-[#111b21] rounded-xl border border-[#2a3942] p-5">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#00a884]" /> Description
                </h3>
                <p className="text-[#8696a0] text-sm leading-relaxed">{selectedTask.description || 'No description provided.'}</p>
              </div>

              {/* Case Details */}
              {caseDetails && (
                <div className="bg-[#111b21] rounded-xl border border-[#2a3942] p-5">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <User className="w-4 h-4 text-[#00a884]" /> Client Details
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <User className="w-4 h-4 text-[#8696a0]" />
                      <div>
                        <p className="text-[#8696a0] text-xs">Name</p>
                        <p className="text-white text-sm">{caseDetails.client_name || 'Anonymous'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-[#8696a0]" />
                      <div>
                        <p className="text-[#8696a0] text-xs">Mobile</p>
                        <p className="text-white text-sm">{caseDetails.client_mobile || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-[#8696a0]" />
                      <div>
                        <p className="text-[#8696a0] text-xs">Location</p>
                        <p className="text-white text-sm">{caseDetails.city || 'Unknown'}, {caseDetails.state || 'Unknown'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="w-4 h-4 text-[#8696a0]" />
                      <div>
                        <p className="text-[#8696a0] text-xs">Created</p>
                        <p className="text-white text-sm">{new Date(caseDetails.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Status */}
              {caseDetails && (
                <div className="bg-[#111b21] rounded-xl border border-[#2a3942] p-5">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#00a884]" /> Payment Status
                  </h3>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 text-xs font-mono uppercase font-bold rounded ${
                      caseDetails.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
                      caseDetails.payment_status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {caseDetails.payment_status || 'pending'}
                    </span>
                    {caseDetails.client_payment_amount && (
                      <span className="text-sm text-[#8696a0]">₹{Number(caseDetails.client_payment_amount).toLocaleString()}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Secure Uploaded Documents */}
              {secureDocs.length > 0 && (
                <div className="bg-[#111b21] rounded-xl border border-[#2a3942] p-5">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#00a884]" /> Secure Documents ({secureDocs.length})
                  </h3>
                  <div className="space-y-2">
                    {secureDocs.map((d) => (
                      <div key={d.id} className="flex items-center justify-between bg-[#202c33] rounded-lg px-3 py-2">
                        <div>
                          <p className="text-white text-sm">{d.display_name || d.file_name}</p>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-[#8696a0] text-xs font-mono uppercase">{d.category}</span>
                            <span className={`text-xs ${d.encrypted !== false ? 'text-green-400' : 'text-yellow-400'}`}>
                              {d.encrypted !== false ? 'Encrypted' : 'Unencrypted'}
                            </span>
                          </div>
                        </div>
                        <a
                          href={`/api/documents/${d.id}/download`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-400 hover:text-blue-300 underline"
                        >
                          Download
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Chat History */}
              {chatHistory.length > 0 && (
                <div className="bg-[#111b21] rounded-xl border border-[#2a3942] p-5">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#00a884]" /> Chat History ({chatHistory.length})
                  </h3>
                  <div className="max-h-64 overflow-y-auto space-y-3">
                    {chatHistory.map((msg, i) => (
                      <div key={i} className={`flex ${msg.sender_type === 'client' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] px-3 py-2 text-sm rounded-xl ${
                          msg.sender_type === 'client'
                            ? 'bg-[#00a884]/20 text-white rounded-tr-sm'
                            : 'bg-[#202c33] text-white rounded-tl-sm'
                        }`}>
                          {msg.content}
                          <div className="text-[10px] text-[#8696a0] mt-1 font-mono">
                            {msg.sender_type} {msg.created_at ? new Date(msg.created_at).toLocaleTimeString() : ''}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Legacy Documents */}
              {artifacts.length > 0 && (
                <div className="bg-[#111b21] rounded-xl border border-[#2a3942] p-5">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#8696a0]" /> Legacy Documents ({artifacts.length})
                  </h3>
                  <div className="space-y-2">
                    {artifacts.map((a) => (
                      <div key={a.id} className="flex items-center justify-between bg-[#202c33] rounded-lg px-3 py-2">
                        <div>
                          <p className="text-white text-sm">{a.file_name}</p>
                          {a.file_size && <p className="text-[#8696a0] text-xs">{Math.round(Number(a.file_size) / 1024)} KB</p>}
                        </div>
                        <a
                          href={`/api/admin/artifacts/${a.id}/download`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-400 hover:text-blue-300 underline"
                        >
                          Download
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Extracted Payload */}
              {payloadData && (
                <div className="bg-[#111b21] rounded-xl border border-[#2a3942] p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#00a884]" /> Extracted Payload
                      <span className="text-[10px] font-mono text-[#8696a0] ml-2">({payloadData.payload_status || 'unknown'})</span>
                    </h3>
                    <button
                      onClick={() => setEditingPayload(!editingPayload)}
                      className="text-xs text-blue-400 hover:text-blue-300 underline"
                    >
                      {editingPayload ? 'Cancel' : 'Edit'}
                    </button>
                  </div>
                  {editingPayload ? (
                    <div className="space-y-3">
                      <textarea
                        value={editedPayload}
                        onChange={(e) => setEditedPayload(e.target.value)}
                        className="w-full h-48 bg-[#202c33] text-white border border-[#374045] rounded-lg text-xs font-mono p-3 resize-none focus:outline-none focus:border-[#00a884]"
                      />
                      <div className="flex gap-2">
                        <button onClick={savePayload} className="px-3 py-1.5 text-xs bg-[#00a884] text-white rounded-lg hover:bg-[#06cf9c]">Save</button>
                        <button onClick={() => setEditingPayload(false)} className="px-3 py-1.5 text-xs bg-[#202c33] text-[#8696a0] rounded-lg hover:bg-[#2a3942]">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="max-h-60 overflow-y-auto text-xs text-[#aebac1] font-mono space-y-1">
                      {renderPayloadFields(payloadData.payload_json)}
                    </div>
                  )}
                </div>
              )}

              {/* Portal Automation */}
              {(autoProfile || autoResult || selectedTask.status !== 'completed') && (
                <div className="bg-[#111b21] rounded-xl border border-[#2a3942] p-5">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <Globe className="w-4 h-4 text-[#00a884]" /> Portal Automation
                  </h3>
                  {autoProfile && (
                    <div className="text-[#8696a0] text-xs mb-3">
                      Profile: {autoProfile.portal_name || autoProfile.document_type || '—'}
                      {autoProfile.portal_url && <span className="ml-2">— {autoProfile.portal_url}</span>}
                      <span className="ml-2">· {autoProfile.field_mapping ? Object.keys(autoProfile.field_mapping).length : 0} fields mapped</span>
                    </div>
                  )}
                  {autoResult && autoResult.error && (
                    <div className="mb-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                      <p className="text-red-400 text-xs">{autoResult.error}</p>
                    </div>
                  )}
                  {autoResult && autoResult.auto_steps && (
                    <div className="mb-3">
                      <p className="text-[#8696a0] text-xs mb-1">Auto-fill steps ({autoResult.auto_steps.length}):</p>
                      <ul className="space-y-1">
                        {autoResult.auto_steps.map((s, i) => (
                          <li key={i} className="flex items-center gap-2 text-xs text-white">
                            <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0" />
                            {typeof s === 'string' ? s : s.description || s.step || `Step ${i + 1}`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {autoResult && autoResult.manual_steps && (
                    <div className="mb-3">
                      <p className="text-[#8696a0] text-xs mb-1">Manual steps needed:</p>
                      <ul className="space-y-1">
                        {autoResult.manual_steps.map((s, i) => (
                          <li key={i} className="flex items-center gap-2 text-xs text-yellow-400">
                            <AlertCircle className="w-3 h-3 flex-shrink-0" />
                            {typeof s === 'string' ? s : s.description || s.step || `Step ${i + 1}`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {autoResult && autoResult.fill_commands && (
                    <details className="mb-3">
                      <summary className="text-[#8696a0] text-xs cursor-pointer hover:text-white">
                        Fill commands ({autoResult.fill_commands.length})
                      </summary>
                      <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                        {autoResult.fill_commands.map((cmd, i) => (
                          <div key={i} className="text-xs text-[#8696a0] font-mono">
                            {cmd.action} → {cmd.selector || cmd.field_name}: "{cmd.value?.slice(0, 40)}"
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                  {selectedTask.status !== 'completed' && !autoResult && (
                    <button onClick={runAutomation} disabled={autoRunning}
                      className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50">
                      {autoRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                      {autoRunning ? 'Running...' : 'Run Portal Automation'}
                    </button>
                  )}
                  {autoResult && !autoResult.error && (
                    <button onClick={runAutomation} disabled={autoRunning}
                      className="flex items-center gap-1 px-4 py-2 bg-[#202c33] text-white text-xs rounded-lg hover:bg-[#2a3942]">
                      <RotateCcw className="w-3 h-3" /> Re-run
                    </button>
                  )}
                </div>
              )}

              {/* Task Notes */}
              <div className="bg-[#111b21] rounded-xl border border-[#2a3942] p-5">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#00a884]" /> Task Notes
                </h3>
                <textarea
                  value={taskNotes}
                  onChange={(e) => setTaskNotes(e.target.value)}
                  className="w-full bg-[#202c33] text-white border border-[#374045] rounded-lg px-3 py-2 text-sm min-h-[100px] resize-none focus:outline-none focus:border-[#00a884]"
                  placeholder="Add your notes, updates, or observations..."
                />
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                {selectedTask.status !== 'completed' && (
                  <>
                    <button 
                      onClick={() => updateTaskStatus('in_progress')}
                      className="px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-500 transition flex items-center gap-2"
                    >
                      <Clock className="w-4 h-4" /> Start Work
                    </button>
                    <button 
                      onClick={() => completeTask(selectedTask.id)}
                      className="px-5 py-2.5 bg-[#00a884] text-white rounded-lg text-sm font-medium hover:bg-[#06cf9c] transition flex items-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" /> Complete Task
                    </button>
                  </>
                )}
                {selectedTask.status === 'completed' && (
                  <button 
                    onClick={() => updateTaskStatus('pending')}
                    className="px-5 py-2.5 bg-yellow-600 text-white rounded-lg text-sm font-medium hover:bg-yellow-500 transition flex items-center gap-2"
                  >
                    <AlertCircle className="w-4 h-4" /> Reopen Task
                  </button>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
